<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import AccordionPanel from '../layouts/AccordionPanel.ce.vue';
import AlertComponent from '@/components/layouts/AlertComponent.ce.vue';
import HeadingTitle from '@/components/layouts/HeadingTitle.ce.vue';
import TranslationValue from '@/components/layouts/TranslationValue.ce.vue';
import PrettyJson from '../layouts/PrettyJson.ce.vue';

import JobChooser from '@/components/forms/JobChooser.ce.vue';
import RegionChooser from '@/components/forms/RegionChooser.ce.vue';

import { useChooserFormStore } from '@/store/forms/chooser';

const apiResponsesStore = useApiResponsesStore();
const { countryResponses } = storeToRefs(apiResponsesStore);

import { useTariffsStore } from '@/store/tariffs';
import { useTranslationsStore } from '@/store/translations';
import {useApiResponsesStore} from "@/store/responses";
import {keys, pick, reduce, remove, values} from "lodash-es";
import {addUp} from "@/utils/calculations";
import AccordionContainer from "@/components/layouts/AccordionContainer.ce.vue";

const tariffsStore = useTariffsStore();
const { hourly, monthly, weekly, yearly } = storeToRefs(tariffsStore);

const translationStore = useTranslationsStore();
const { formatCurrency } = storeToRefs(translationStore);

const formsStore = useChooserFormStore();
const { debug, formData } = storeToRefs(formsStore);



const ready = computed(() => {
    return formData.value.region.code &&
        yearly.value.livingWages.withoutOverhead &&
        countryResponses.value.socialSecurityTaxes.withoutOverhead;
});

const debugReadyContext = computed(() => {
    const ctx = {
        'formData.region.code': formData.value.region.code,
        'yearly.livingWages.withoutOverhead': null,
        'countryResponses.socialSecurityTaxes.withoutOverhead': countryResponses.value.socialSecurityTaxes.withoutOverhead
    };
    if (yearly.value.livingWages) {
        ctx['yearly.livingWages.withoutOverhead'] = yearly.value.livingWages.withoutOverhead;
    }
    return ctx;
})

const taxAndContributions = computed(() => {
    const amounts = {
        hourly: null,
        monthly: null,
        yearly: null,
    }

    const socialContributionsFields = remove(
        keys(countryResponses.value.socialSecurityTaxes.withoutOverhead['social_contributions']),
        function (f) {
            return f !== 'total';
        }
    );
    const totalContrib = reduce(
        values(
            pick(
                countryResponses.value.socialSecurityTaxes.withoutOverhead['social_contributions'],
                socialContributionsFields
            )
        ),
        addUp
    );

    amounts.yearly = countryResponses.value.socialSecurityTaxes.withoutOverhead['income_tax'] + totalContrib;
    amounts.monthly = amounts['yearly'] / 12;
    amounts.hourly = amounts['monthly'] / (4.333 * weekly.value.fullTimeWorkingHours);
    return amounts;
})

const alertTranslationContext = computed(() => {


    return {
        hr_net_lw: formatCurrency.value(
            hourly.value.livingWages.withoutOverhead
            + taxAndContributions.value.hourly
        ),
        lcu: formatCurrency.value(
            monthly.value.livingWages.withoutOverhead
            + taxAndContributions.value.monthly
        ),
        year_net_lw: formatCurrency.value(
            yearly.value.livingWages.withoutOverhead
            + taxAndContributions.value.yearly
        ),
    };
});
</script>

<template>
    <HeadingTitle>
        <TranslationValue id="page1_heading_ltt" />
    </HeadingTitle>

    <hr class="mb-2" />

    <p class="mb-2">
        <TranslationValue id="page1_lt_for_job_region" />
    </p>

    <RegionChooser />

    <AccordionContainer v-if="debug" class="mt-8">
        <AccordionPanel :expanded="false">
            <template #title>Explainer amounts debugger</template>
            <template #content>
                <div class="mt-2">
                    <label class="block">Prerequisites for calculations:</label><br/>
                    <PrettyJson :obj="debugReadyContext"/>
                </div>
                <template v-if="ready">
                    <div class="mt-2">
                        <label class="block">Social contrib and taxes:</label><br/>
                        <PrettyJson :obj="taxAndContributions"/>
                    </div>
                    <div class="mt-2">
                        <label class="block">Translation context:</label><br/>
                        <PrettyJson :obj="alertTranslationContext"/>
                    </div>
                </template>
            </template>
        </AccordionPanel>
    </AccordionContainer>

    <AlertComponent v-if="ready" class="my-8">

        <TranslationValue
            id="page1_expla_lt"
            :context="alertTranslationContext"
        />
        <ul class="list-disc list-inside my-4">
            <li>
                <TranslationValue
                    id="page1_expla_lt_phour"
                    :context="alertTranslationContext"
                />
            </li>
            <li>
                <TranslationValue
                    id="page1_expla_lt_pmnth"
                    :context="alertTranslationContext"
                />
            </li>
            <li>
                <TranslationValue
                    id="page1_expla_lt_pyear"
                    :context="alertTranslationContext"
                />
            </li>
        </ul>

        <TranslationValue
            id="page1_expla_lt_note"
            :context="alertTranslationContext"
        />


    </AlertComponent>

    <JobChooser />
</template>
